import { Injectable } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet-control-geocoder/dist/Control.Geocoder.js';
import * as Geocoder from 'leaflet-control-geocoder';
import { GeocodeResultsModalComponent } from '../components/geocode-results-modal/geocode-results-modal.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SharedService } from './shared.service';
import { HttpClient } from '@angular/common/http';

@Injectable({
    providedIn: 'root',
})
export class MapService extends SharedService {
    // GUARDADO DE MAPAS Y MARCADORES
    private maps: { [key: string]: L.Map } = {};
    private markers: { [key: string]: L.Marker } = {};
    private allMarkers: { [key: string]: L.Marker[] } = {};
    private dialogRef: MatDialogRef<GeocodeResultsModalComponent> | null = null;

    // ICONO PERSONALIZADO ( PUNTO DEL MAPA )
    customIcon = L.divIcon({
        className: 'custom-marker',
        html: `
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>
          </svg>
        `,
        iconSize: [30, 30],
    });

    constructor(http: HttpClient, private dialog: MatDialog) {
        super(http, 'maps');
    }

    // CREAR EL MAPA EN PANTALLA
    initMap(containerId: string, options: L.MapOptions, search: boolean = false, canClick: boolean = true): L.Map {
        if (this.maps[containerId]) {
            return this.maps[containerId];
        }

        const map = L.map(containerId, options);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(map);

        this.maps[containerId] = map;

        if (search) {
            const geocoder = (Geocoder as any)
                .geocoder({
                    defaultMarkGeocode: false,
                })
                .addTo(map);

            geocoder.on('startgeocode', (e: any) => {
                //console.log('Dirección ingresada:', e.input);
            });

            geocoder.on('markgeocode', (e: any) => {
                const latlng = e.geocode.center;
                // this.addMarker(containerId, latlng);
                // this.getAddressFromCoordinates(lat, lng);
            });
        }
        map.on('zoom', () => {
            const zoomLevel = map.getZoom();
            const scale = this.calculateScale(zoomLevel);
            const markers = document.querySelectorAll('.custom-marker svg') as NodeListOf<SVGElement>;

            markers.forEach((marker) => {
                marker.style.transform = `scale(${scale})`;
            });
        });
        if (canClick) this.captureCoordinatesOnClick(containerId);

        // Agregar accesibilidad a los controles de Leaflet después de que se inicialicen
        setTimeout(() => {
            this.addAccessibilityToLeafletControls(containerId);
        }, 1000);

        // Agregar accesibilidad adicional después de que los iframes se carguen
        setTimeout(() => {
            this.addAccessibilityToIframeImages(containerId);
        }, 2000);

        // Ejecutar accesibilidad adicional para elementos que se cargan dinámicamente
        setTimeout(() => {
            this.addAccessibilityToLeafletControls(containerId);
        }, 3000);

        return map;
    }

    // ESCUCHA CLICS DEL MAPA
    captureCoordinatesOnClick(containerId: string): void {
        if (!this.maps[containerId]) {
            console.error(`El mapa con el ID "${containerId}" no está inicializado.`);
            return;
        }

        this.maps[containerId].on('click', (event: L.LeafletMouseEvent) => {
            const { lat, lng } = event.latlng;
            this.addMarker(containerId, [lat, lng]);
            this.getAddressFromCoordinates(lat, lng, true);
        });
    }
    destroyMap(containerId: string): void {
        if (this.maps[containerId]) {
            this.maps[containerId].remove();
            delete this.maps[containerId];
        }
    }

    // AGREGA UN MARCADOR
    addMarker(containerId: string, latlng: any, content?: any, draggable: boolean = true): void {
        if (!this.maps[containerId]) {
            return;
        }

        if (this.markers[containerId]) {
            this.maps[containerId].removeLayer(this.markers[containerId]);
        }
        const marker = L.marker(latlng, { icon: this.customIcon, draggable }).addTo(this.maps[containerId]).bindPopup(content).openPopup();
        this.markers[containerId] = marker;
        this.maps[containerId].setView(latlng, 16);
        //this.getAddressFromCoordinates(latlng.lat, latlng.lng);
        marker.on('moveend', (event) => {
            const movedMarker = event.target;
            const newLatLng = movedMarker.getLatLng();
            this.getAddressFromCoordinates(newLatLng.lat, newLatLng.lng, true);
        });
    }

    // ELIMINAR MARCADORES
    deleteMarkers(containerId: string): void {
        if (this.maps[containerId]) {
            Object.values(this.markers).forEach((marker) => {
                this.maps[containerId].removeLayer(marker);
            });
            this.markers = {};
        }
    }

    // AGREGA MUCHOS MARCADORES
    addMarkers(containerId: string, locations: Array<{ log: number; lat: number }>): void {
        if (!this.maps[containerId]) return;

        locations.forEach((location) => {
            const latlng = [location.lat, location.log] as L.LatLngTuple;
            const marker = L.marker(latlng, { icon: this.customIcon, draggable: false }).addTo(this.maps[containerId]);
            this.markers[containerId] = marker;
            if (!this.allMarkers[containerId]) {
                this.allMarkers[containerId] = [];
            }
            this.allMarkers[containerId].push(marker);
        });
    }

    getAddressFromCoordinates(lat: any, lng: any, eventEmmiter: boolean = false): void {
        if (!lat && !lng) return;

        const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`;

        fetch(url)
            .then((response) => response.json())
            .then((data) => {
                if (data && data.display_name) {
                    Object.values(this.markers).forEach((marker) => {
                        marker.bindPopup(`<b>${data.display_name}</b><p>Latitud: <b>${data.lat}</b></p><p>Longitud: <b>${data.lon}</b></p>`).openPopup();
                    });
                    if (eventEmmiter) {
                        this.dispatchData({ eventMap: true, data });
                    }
                } else {
                    console.log('No se encontró una dirección para estas coordenadas.');
                }
            })
            .catch((error) => {
                console.error('Error al obtener la dirección:', error);
            });
    }

    getCurrentLocation(containerId: string): void {
        if (navigator.geolocation) {
            navigator.geolocation.watchPosition(
                (position) => {
                    const latlng = [position.coords.latitude, position.coords.longitude] as L.LatLngTuple;
                    this.maps[containerId].setView(latlng, 13);
                    this.addMarker(containerId, latlng);
                },
                (error) => {
                    console.error('Error al obtener la ubicación en tiempo real:', error);
                }
            );
        }
    }
    searchAddress(map: string, address: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const mapContent = this.maps[map];
            const geocoder = new (Geocoder as any).Geocoder.Nominatim();

            geocoder.geocode(address, (results: any) => {
                if (results.length > 0) {
                    if (this.dialogRef) this.dialogRef.close();

                    this.dialogRef = this.dialog.open(GeocodeResultsModalComponent, {
                        width: '400px',
                        data: { results },
                    });

                    this.dialogRef.afterClosed().subscribe((selectedResult: any) => {
                        if (selectedResult) {
                            this.addMarker(map, selectedResult.center, selectedResult.name);
                            resolve(selectedResult.properties);
                        } else {
                            resolve(null);
                        }
                    });
                } else {
                    reject('No se encontraron resultados para la dirección proporcionada.');
                }
            });
        });
    }
    private calculateScale(zoomLevel: number): number {
        const baseScale = 1;
        const scaleFactor = 0.1;
        return baseScale + (zoomLevel - 13) * scaleFactor;
    }

    //Funcionalidades nuevas implementadas para mayor escalabilidad

    initMap2(containerId: string, options: L.MapOptions, search: boolean = false, canClick: boolean = true): L.Map {
        if (this.maps[containerId]) {
            return this.maps[containerId];
        }

        const map = L.map(containerId, options);

        L.control
            .zoom({
                position: 'topright',
            })
            .addTo(map);

        L.control
            .scale({
                position: 'bottomleft',
                metric: true,
                imperial: true,
            })
            .addTo(map);

        L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
            maxZoom: 19,
        }).addTo(map);

        this.maps[containerId] = map;

        if (search) {
            const geocoder = (Geocoder as any)
                .geocoder({
                    defaultMarkGeocode: false,
                })
                .addTo(map);

            geocoder.on('startgeocode', (e: any) => {
                //console.log('Dirección ingresada:', e.input);
            });

            geocoder.on('markgeocode', (e: any) => {
                const latlng = e.geocode.center;
                // this.addMarker(containerId, latlng);
                // this.getAddressFromCoordinates(lat, lng);
            });
        }
        map.on('zoom', () => {
            const zoomLevel = map.getZoom();
            const scale = this.calculateScale(zoomLevel);
            const markers = document.querySelectorAll('.custom-marker svg') as NodeListOf<SVGElement>;

            markers.forEach((marker) => {
                marker.style.transform = `scale(${scale})`;
            });
        });
        if (canClick) this.captureCoordinatesOnClick(containerId);

        // Agregar accesibilidad a los controles de Leaflet después de que se inicialicen
        setTimeout(() => {
            this.addAccessibilityToLeafletControls(containerId);
        }, 1000);

        // Agregar accesibilidad adicional después de que los iframes se carguen
        setTimeout(() => {
            this.addAccessibilityToIframeImages(containerId);
        }, 2000);

        // Ejecutar accesibilidad adicional para elementos que se cargan dinámicamente
        setTimeout(() => {
            this.addAccessibilityToLeafletControls(containerId);
        }, 3000);

        return map;
    }

    getMap(containerId: string): L.Map | undefined {
        return this.maps[containerId];
    }

    //Metodo de marcadores corregido
    deleteAllMarkers(containerId: string): void {
        const markers = this.allMarkers[containerId];
        if (this.maps[containerId] && markers?.length) {
            markers.forEach((marker) => {
                this.maps[containerId].removeLayer(marker);
            });

            this.allMarkers[containerId] = [] as L.Marker[];
        }
    }

    // Agregar accesibilidad a los controles de Leaflet
    private addAccessibilityToLeafletControls(containerId: string): void {
        const mapContainer = document.getElementById(containerId);
        if (!mapContainer) return;

        // Buscar todos los enlaces en los controles de Leaflet, incluyendo iframes
        const leafletLinks = mapContainer.querySelectorAll('.leaflet-draw-section a, .leaflet-control a, iframe .leaflet-draw-section a, iframe .leaflet-control a');
        
        leafletLinks.forEach((link: Element) => {
            const anchor = link as HTMLAnchorElement;
            
            // Solo agregar accesibilidad si no tiene atributos de accesibilidad
            if (!anchor.getAttribute('aria-label') && !anchor.getAttribute('title')) {
                // Determinar el tipo de control basado en las clases o íconos
                const icon = anchor.querySelector('i, span');
                if (icon) {
                    const iconClass = icon.className;
                    
                    // Asignar etiquetas descriptivas basadas en el ícono
                    if (iconClass.includes('fa-map-pin') || iconClass.includes('fa-plus')) {
                        anchor.setAttribute('aria-label', 'Agregar marcador al mapa');
                        anchor.setAttribute('title', 'Agregar marcador al mapa');
                    } else if (iconClass.includes('fa-edit') || iconClass.includes('fa-pencil')) {
                        anchor.setAttribute('aria-label', 'Editar elemento del mapa');
                        anchor.setAttribute('title', 'Editar elemento del mapa');
                    } else if (iconClass.includes('fa-trash') || iconClass.includes('fa-times')) {
                        anchor.setAttribute('aria-label', 'Eliminar elemento del mapa');
                        anchor.setAttribute('title', 'Eliminar elemento del mapa');
                    } else if (iconClass.includes('fa-zoom-in')) {
                        anchor.setAttribute('aria-label', 'Acercar mapa');
                        anchor.setAttribute('title', 'Acercar mapa');
                    } else if (iconClass.includes('fa-zoom-out')) {
                        anchor.setAttribute('aria-label', 'Alejar mapa');
                        anchor.setAttribute('title', 'Alejar mapa');
                    } else {
                        anchor.setAttribute('aria-label', 'Control de mapa');
                        anchor.setAttribute('title', 'Control de mapa');
                    }
                }
            }
        });

        // Buscar todos los elementos de formulario en los controles de Leaflet
        const leafletFormElements = mapContainer.querySelectorAll('.leaflet-control input, .leaflet-control select, .leaflet-control textarea');
        
        leafletFormElements.forEach((element: Element) => {
            const formElement = element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
            
            // Solo agregar accesibilidad si no tiene atributos de accesibilidad
            if (!formElement.getAttribute('aria-label') && !formElement.getAttribute('aria-labelledby') && !formElement.getAttribute('title')) {
                const className = formElement.className;
                
                // Asignar etiquetas descriptivas basadas en las clases
                if (className.includes('leaflet-layerstree-sel-all-checkbox')) {
                    formElement.setAttribute('aria-label', 'Seleccionar todas las capas del mapa');
                    formElement.setAttribute('title', 'Seleccionar todas las capas del mapa');
                } else if (className.includes('leaflet-control-layers-selector')) {
                    formElement.setAttribute('aria-label', 'Mostrar/ocultar capa del mapa');
                    formElement.setAttribute('title', 'Mostrar/ocultar capa del mapa');
                } else if (formElement.type === 'checkbox') {
                    formElement.setAttribute('aria-label', 'Opción de capa del mapa');
                    formElement.setAttribute('title', 'Opción de capa del mapa');
                } else if (formElement.type === 'radio') {
                    formElement.setAttribute('aria-label', 'Seleccionar opción de mapa');
                    formElement.setAttribute('title', 'Seleccionar opción de mapa');
                } else if (formElement.tagName === 'SELECT') {
                    formElement.setAttribute('aria-label', 'Selector de opciones del mapa');
                    formElement.setAttribute('title', 'Selector de opciones del mapa');
                } else if (formElement.tagName === 'TEXTAREA') {
                    formElement.setAttribute('aria-label', 'Campo de texto del mapa');
                    formElement.setAttribute('title', 'Campo de texto del mapa');
                } else {
                    formElement.setAttribute('aria-label', 'Campo de entrada del mapa');
                    formElement.setAttribute('title', 'Campo de entrada del mapa');
                }
            }
        });

        // Buscar todas las imágenes en los controles de Leaflet, incluyendo iframes
        const leafletImages = mapContainer.querySelectorAll('.leaflet-control img, .leaflet-draw-section img, iframe img, .img-fluid');
        
        leafletImages.forEach((element: Element) => {
            const img = element as HTMLImageElement;
            
            // Solo agregar accesibilidad si no tiene atributos de accesibilidad
            if (!img.getAttribute('alt') && !img.getAttribute('aria-label') && !img.getAttribute('aria-labelledby') && !img.getAttribute('title')) {
                const src = img.src;
                const className = img.className;
                
                // Asignar texto alternativo basado en la fuente o clase
                if (src.includes('compass.png')) {
                    img.setAttribute('alt', 'Brújula del mapa');
                    img.setAttribute('title', 'Brújula del mapa');
                } else if (src.includes('zoom-in') || className.includes('zoom-in')) {
                    img.setAttribute('alt', 'Acercar mapa');
                    img.setAttribute('title', 'Acercar mapa');
                } else if (src.includes('zoom-out') || className.includes('zoom-out')) {
                    img.setAttribute('alt', 'Alejar mapa');
                    img.setAttribute('title', 'Alejar mapa');
                } else if (src.includes('layers') || className.includes('layers')) {
                    img.setAttribute('alt', 'Control de capas del mapa');
                    img.setAttribute('title', 'Control de capas del mapa');
                } else if (src.includes('marker') || className.includes('marker')) {
                    img.setAttribute('alt', 'Marcador del mapa');
                    img.setAttribute('title', 'Marcador del mapa');
                } else if (src.includes('draw') || className.includes('draw')) {
                    img.setAttribute('alt', 'Herramienta de dibujo del mapa');
                    img.setAttribute('title', 'Herramienta de dibujo del mapa');
                } else {
                    img.setAttribute('alt', 'Elemento del mapa');
                    img.setAttribute('title', 'Elemento del mapa');
                }
            }
        });
    }

    // Agregar accesibilidad específica a imágenes dentro de iframes
    private addAccessibilityToIframeImages(containerId: string): void {
        const mapContainer = document.getElementById(containerId);
        if (!mapContainer) return;

        // Buscar todos los iframes dentro del contenedor del mapa
        const iframes = mapContainer.querySelectorAll('iframe');
        
        iframes.forEach((iframe: HTMLIFrameElement) => {
            try {
                // Acceder al contenido del iframe (solo si es del mismo origen)
                const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                if (iframeDoc) {
                    // Buscar imágenes dentro del iframe
                    const iframeImages = iframeDoc.querySelectorAll('img');
                    
                    iframeImages.forEach((img: HTMLImageElement) => {
                        // Solo agregar accesibilidad si no tiene atributos de accesibilidad
                        if (!img.getAttribute('alt') && !img.getAttribute('aria-label') && !img.getAttribute('aria-labelledby') && !img.getAttribute('title')) {
                            const src = img.src;
                            const className = img.className;
                            
                            // Asignar texto alternativo basado en la fuente o clase
                            if (src.includes('compass.png')) {
                                img.setAttribute('alt', 'Brújula del mapa');
                                img.setAttribute('title', 'Brújula del mapa');
                            } else if (src.includes('zoom-in') || className.includes('zoom-in')) {
                                img.setAttribute('alt', 'Acercar mapa');
                                img.setAttribute('title', 'Acercar mapa');
                            } else if (src.includes('zoom-out') || className.includes('zoom-out')) {
                                img.setAttribute('alt', 'Alejar mapa');
                                img.setAttribute('title', 'Alejar mapa');
                            } else if (src.includes('layers') || className.includes('layers')) {
                                img.setAttribute('alt', 'Control de capas del mapa');
                                img.setAttribute('title', 'Control de capas del mapa');
                            } else if (src.includes('marker') || className.includes('marker')) {
                                img.setAttribute('alt', 'Marcador del mapa');
                                img.setAttribute('title', 'Marcador del mapa');
                            } else if (src.includes('draw') || className.includes('draw')) {
                                img.setAttribute('alt', 'Herramienta de dibujo del mapa');
                                img.setAttribute('title', 'Herramienta de dibujo del mapa');
                            } else if (className.includes('img-fluid')) {
                                img.setAttribute('alt', 'Elemento del mapa');
                                img.setAttribute('title', 'Elemento del mapa');
                            } else {
                                img.setAttribute('alt', 'Elemento del mapa');
                                img.setAttribute('title', 'Elemento del mapa');
                            }
                        }
                    });

                    // Buscar enlaces dentro del iframe
                    const iframeLinks = iframeDoc.querySelectorAll('a');
                    
                    iframeLinks.forEach((link: HTMLAnchorElement) => {
                        // Solo agregar accesibilidad si no tiene atributos de accesibilidad
                        if (!link.getAttribute('aria-label') && !link.getAttribute('title')) {
                            // Determinar el tipo de control basado en las clases o íconos
                            const icon = link.querySelector('i, span');
                            if (icon) {
                                const iconClass = icon.className;
                                
                                // Asignar etiquetas descriptivas basadas en el ícono
                                if (iconClass.includes('fa-map-pin') || iconClass.includes('fa-plus')) {
                                    link.setAttribute('aria-label', 'Agregar marcador al mapa');
                                    link.setAttribute('title', 'Agregar marcador al mapa');
                                } else if (iconClass.includes('fa-edit') || iconClass.includes('fa-pencil')) {
                                    link.setAttribute('aria-label', 'Editar elemento del mapa');
                                    link.setAttribute('title', 'Editar elemento del mapa');
                                } else if (iconClass.includes('fa-trash') || iconClass.includes('fa-times')) {
                                    link.setAttribute('aria-label', 'Eliminar elemento del mapa');
                                    link.setAttribute('title', 'Eliminar elemento del mapa');
                                } else if (iconClass.includes('fa-zoom-in')) {
                                    link.setAttribute('aria-label', 'Acercar mapa');
                                    link.setAttribute('title', 'Acercar mapa');
                                } else if (iconClass.includes('fa-zoom-out')) {
                                    link.setAttribute('aria-label', 'Alejar mapa');
                                    link.setAttribute('title', 'Alejar mapa');
                                } else if (iconClass.includes('fa-layers') || iconClass.includes('fa-layer-group')) {
                                    link.setAttribute('aria-label', 'Control de capas del mapa');
                                    link.setAttribute('title', 'Control de capas del mapa');
                                } else if (iconClass.includes('fa-draw') || iconClass.includes('fa-pen')) {
                                    link.setAttribute('aria-label', 'Herramienta de dibujo del mapa');
                                    link.setAttribute('title', 'Herramienta de dibujo del mapa');
                                } else {
                                    link.setAttribute('aria-label', 'Control de mapa');
                                    link.setAttribute('title', 'Control de mapa');
                                }
                            } else {
                                // Si no hay ícono, agregar etiqueta genérica
                                link.setAttribute('aria-label', 'Control de mapa');
                                link.setAttribute('title', 'Control de mapa');
                            }
                        }
                    });
                }
            } catch (error) {
                // Si no se puede acceder al iframe (diferente origen), ignorar silenciosamente
                console.debug('No se puede acceder al contenido del iframe para agregar accesibilidad:', error);
            }
        });
    }
}
